import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import Modal from './Modal';
import Box from '../components/shared/atoms/Box';
import Text from '../components/shared/atoms/Text';
import Heading from '../components/shared/atoms/Heading';
import Input from '../components/shared/atoms/Input';
import Button from '../components/shared/atoms/Button';
import Alert from '../components/shared/molecules/Alert';
import Phone24 from '../components/icons/Phone24';

import * as api from '../utils/api';
import { setClient as setClientAction } from '../actions/client';

const PLACEHOLDER_PHONE = '0000000000';
const phoneRegExp = /^(0[67]\d{8}|\+\d{11})$/;

// Retire les séparateurs de saisie (espaces, points, tirets, parenthèses) et
// convertit le préfixe 00 en +, pour valider et enregistrer un numéro compact.
export const normalizePhone = (value) =>
  String(value || '')
    .replace(/[\s.\-()]/g, '')
    .replace(/^00/, '+');

/**
 * Modal bloquante pour demander le numéro de téléphone.
 * Affichée automatiquement quand le client a un téléphone placeholder (0000000000).
 * Ne peut pas être fermée sans saisir un numéro valide.
 */
const PhoneRequiredModal = ({ open, onUpdated }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const client = useSelector((state) => state.client);

  const [phone, setPhone] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation: champ requis
    const normalizedPhone = normalizePhone(phone);

    if (!normalizedPhone) {
      setError(t('phoneRequired.error.required', 'Ce champ est obligatoire'));
      return;
    }

    // Validation: format téléphone
    if (!phoneRegExp.test(normalizedPhone)) {
      setError(
        t('phoneRequired.error.invalid', 'Numéro de téléphone invalide'),
      );
      return;
    }

    // Validation: pas de placeholder
    if (normalizedPhone === PLACEHOLDER_PHONE) {
      setError(
        t('phoneRequired.error.invalid', 'Numéro de téléphone invalide'),
      );
      return;
    }

    if (!client || !client.id) {
      setError(
        t(
          'phoneRequired.error.generic',
          'Une erreur est survenue, veuillez réessayer.',
        ),
      );
      return;
    }

    try {
      setLoading(true);
      const response = await api.put(`/clients/${client.id}`, {
        tel: normalizedPhone,
      });
      setLoading(false);

      if (response && response.id) {
        // Mise à jour du client dans le store Redux
        dispatch(setClientAction(response));

        // Callback pour informer le parent
        if (onUpdated) {
          onUpdated(response);
        }
      } else if (response && response.errors && response.errors.tel) {
        const telErrors = Array.isArray(response.errors.tel)
          ? response.errors.tel.join(' ')
          : response.errors.tel;
        setError(telErrors);
      } else {
        setError(
          t(
            'phoneRequired.error.generic',
            'Une erreur est survenue, veuillez réessayer.',
          ),
        );
      }
    } catch (err) {
      setLoading(false);
      setError(
        t(
          'phoneRequired.error.generic',
          'Une erreur est survenue, veuillez réessayer.',
        ),
      );
    }
  };

  // Empêcher la fermeture de la modal (pas de onRequestClose fonctionnel)
  const handleRequestClose = () => {
    // Ne rien faire - la modal est bloquante
  };

  return (
    <Modal
      open={open}
      onRequestClose={handleRequestClose}
      hasCloseAction={false}
      width="small"
      zIndex={1010}>
      <Box p={{ xs: 2, lg: 3 }}>
        <Box mb={4} display="flex" alignItems="center">
          <Phone24 color="primary.main" />
          <Heading level={4} ml={2}>
            {t('phoneRequired.title', 'Numéro de téléphone requis')}
          </Heading>
        </Box>

        <Text mb={4} color="gray.darker">
          {t(
            'phoneRequired.subtitle',
            'Pour finaliser votre inscription et pouvoir réserver une réparation, nous avons besoin de votre numéro de téléphone.',
          )}
        </Text>

        <Text mb={4} color="gray.dark" variant="caption">
          {t(
            'phoneRequired.reassurance',
            "Nous l'utilisons uniquement pour vous prévenir en cas de changement sur votre rendez-vous et pour transmettre les informations à votre réparateur. Pas de spam, pas de démarchage.",
          )}
        </Text>

        {error && (
          <Alert status="danger" mb={3}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Input
            mb={4}
            color="black"
            bg="gray.lightest"
            before={<Phone24 />}
            placeholder={t('phone.placeholder', '06 12 34 56 78')}
            label={t('phone.inputlabel', 'Téléphone')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            data-testid="phone-required-input"
          />

          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="flex-end"
            mt={4}>
            <Button
              type="submit"
              loading={loading}
              variant="filled"
              data-testid="phone-required-submit">
              {t('phoneRequired.submit', 'Valider mon numéro')}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

PhoneRequiredModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onUpdated: PropTypes.func,
};

PhoneRequiredModal.defaultProps = {
  onUpdated: null,
};

export default PhoneRequiredModal;
